import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createManifest } from '../api/manifests-api';

interface CreateManifestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateManifestModal({ isOpen, onClose, onCreated }: CreateManifestModalProps) {
  const navigate = useNavigate();
  const [bargeName, setBargeName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await createManifest({ bargeName });
      if (response.success && response.data) {
        setBargeName('');
        onCreated();
        onClose();
        // Navigate to the newly created manifest
        navigate(`/manifests/${response.data.id}`);
      } else {
        setError(response.message || 'Failed to create manifest');
      }
    } catch {
      setError('An error occurred while creating manifest');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700">
        <h2 className="text-xl font-semibold mb-4">Create Manifest</h2>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="bargeName" className="block text-sm font-medium mb-2">
              Barge Name
            </label>
            <input
              id="bargeName"
              type="text"
              value={bargeName}
              onChange={(e) => setBargeName(e.target.value)}
              required
              className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
              placeholder="Enter barge name"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed rounded transition-colors"
            >
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
